import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgMultiLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { UserInfo } from '../../sdk/sdk';
import { CalendarRole } from '../../models/calendar-role';
import { CalendarService } from '../../services/calendar.service';
import { CrewScheduleStore } from '../../services/crew-schedule-store.service';

@Component({
  selector: 'app-foreman-selector',
  imports: [
    NgSelectComponent,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgMultiLabelTemplateDirective,
  ],
  templateUrl: './foreman-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForemanSelectorComponent implements OnInit, OnChanges {
  private readonly calendarService = inject(CalendarService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() branchId: string | null = null;
  @Input() disabled = false;
  /** When provided (e.g. from side-modal pending state), overrides the
   * CalendarService subscription as the role-filter source so the name list
   * reacts to in-flight role changes before Apply is clicked. */
  @Input() roles: readonly CalendarRole[] | null = null;
  @Output() foremenSelected = new EventEmitter<string[]>();

  allSelected = false;
  foremen: UserInfo[] = [];
  isLoading = false;
  formControl = new FormControl<string[]>([]);

  /** Full roster from the API; `foremen` is `allForemen` filtered by the
   * currently-selected calendar roles. Kept separate so role-narrowing is
   * a re-filter, not a re-fetch. */
  private allForemen: UserInfo[] = [];
  private selectedRoles: readonly CalendarRole[] = [];

  constructor() {
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((selectedIds) => {
        this.updateAllSelectedFlag();
        this.foremenSelected.emit(selectedIds ?? []);
      });

    this.calendarService.getDateRange()
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe(() => this.loadForemen());

    this.calendarService.getSelectedRoles()
      .pipe(takeUntilDestroyed())
      .subscribe((roles) => {
        // Only use the service subscription when no Input override is provided.
        // When the side-modal passes `roles` as an Input, it owns the filter source.
        if (this.roles === null) {
          this.selectedRoles = roles;
          this.applyRoleFilter();
        }
      });
  }

  ngOnInit(): void {
    const appliedIds = this.calendarService.getCurrentSelectedForemenIds();
    if (appliedIds.length > 0) {
      this.formControl.patchValue(appliedIds, { emitEvent: false });
      this.updateAllSelectedFlag();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['branchId']) {
      this.loadForemen();
    }
    if (changes['disabled']) {
      if (this.disabled) {
        this.formControl.disable({ emitEvent: false });
      } else {
        this.formControl.enable({ emitEvent: false });
      }
    }
    if (changes['roles'] && this.roles !== null) {
      this.selectedRoles = this.roles;
      this.applyRoleFilter();
    }
  }

  onSelectionChange(_: unknown): void {
    this.updateAllSelectedFlag();
  }

  clearAll(): void {
    this.formControl.patchValue([]);
    this.allSelected = false;
  }

  resetSelection(): void {
    this.foremen = [];
    this.allForemen = [];
    this.formControl.patchValue([], { emitEvent: false });
    this.allSelected = false;
  }

  private loadForemen(): void {
    if (!this.branchId) {
      this.allForemen = [];
      this.foremen = [];
      return;
    }

    const dateRange = this.calendarService.getCurrentDateRange();
    if (!dateRange) {
      this.allForemen = [];
      this.foremen = [];
      return;
    }

    this.isLoading = true;
    this.calendarService
      .getForemen(this.branchId, dateRange.startDate, dateRange.endDate)
      .subscribe({
        next: (foremen) => {
          this.allForemen = [...foremen].sort((a, b) => a.name!.localeCompare(b.name!));
          this.isLoading = false;
          this.applyRoleFilter();
          this.cdr.markForCheck();
        },
        error: () => {
          this.allForemen = [];
          this.foremen = [];
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Narrow the visible source list to personnel whose role (derived from
   * `UserInfo.department`) is in the active calendar-role selection, and
   * drop any currently-selected ids that no longer match. Mirrors the
   * role classification used by `CrewScheduleStore` so the selector and
   * the calendar agree on what each person is.
   */
  private applyRoleFilter(): void {
    this.foremen = this.allForemen.filter((f) =>
      this.selectedRoles.includes(CrewScheduleStore.classifyRole(f.department)),
    );
    // Selection is intentionally NOT trimmed here. IDs outside the current
    // visible list are kept in formControl.value so that re-expanding a role
    // restores the user's earlier picks without them having to re-select.
    // Trimming to valid-under-applied-roles happens in side-modal.applySettings().
    this.updateAllSelectedFlag();
    this.cdr.markForCheck();
  }

  private updateAllSelectedFlag(): void {
    const selectedCount = this.formControl.value?.length ?? 0;
    this.allSelected = selectedCount > 0 && selectedCount === this.foremen.length;
  }
}
